import React from "react";
import { Compass, MapPin, Phone } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProviderMetrics from "./provider-metrics";
import type { ProviderSearchResultItemType } from "@/types";
import { formatPhoneNumber } from "@/lib/phone-utils";

type ProviderDetailsProps = {
  providerDetail: ProviderSearchResultItemType;
  index: number;
  openInMapview?: (index: number) => void;
  getDirection?: (providerLat: number, providerLng: number) => void;
  callProvider?: (phoneNumber: string) => void;
};

const ProviderDetails: React.FC<ProviderDetailsProps> = ({
  providerDetail,
  index,
  openInMapview,
  getDirection,
  callProvider,
}) => {
  return (
    <Card className="mx-auto w-full bg-white shadow-sm">
      <CardContent className="p-4">
        {/* Header Section */}
        <div className="mb-4 flex items-start justify-between border-b border-gray-200 pb-2">
          <h1 className="text-md flex-1 pr-4 font-semibold text-gray-900">
            {providerDetail?.name && providerDetail?.name}
          </h1>
          {providerDetail?.providerMetrics && (
            <ProviderMetrics scores={providerDetail?.providerMetrics} />
          )}
        </div>
        {/* Specialty and Network Tags */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {providerDetail?.typeDesc && (
            <Badge
              variant="secondary"
              className="rounded-full bg-gray-100 text-gray-700 hover:bg-gray-100"
            >
              {providerDetail?.typeDesc?.toUpperCase()}
            </Badge>
          )}
          {providerDetail?.speciality && (
            <Badge
              variant="secondary"
              className="rounded-full bg-gray-100 text-gray-700 hover:bg-gray-100"
            >
              {providerDetail?.speciality?.toUpperCase()}
            </Badge>
          )}
          {providerDetail?.prvExperience && (
            <Badge
              className="rounded-full"
              style={{
                backgroundColor: providerDetail?.color || "#F3F4F6",
                color: providerDetail?.color ? "#ffffff" : "#374151",
              }}
            >
              {providerDetail?.networkTag?.toUpperCase()}
            </Badge>
          )}
        </div>

        {/* Address and Distance Section */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex gap-4">
              <p className="mb-1 text-sm text-gray-600">
                {providerDetail?.address && providerDetail?.address}
              </p>
              <p className="text-sm text-gray-500">
                •{" "}
                {providerDetail?.distance && providerDetail?.distance < 1
                  ? Math.round(providerDetail?.distance * 10) / 10
                  : Math.round(providerDetail?.distance)}{" "}
                miles (approx)
              </p>
            </div>
            <span className="text-sm text-gray-500">
              {formatPhoneNumber(providerDetail?.phone)}
            </span>
          </div>
          {/* Action Buttons */}
          <div className="ml-4 flex items-center gap-2">
            {/*
              4.1.2 Name, Role, Value — A. All three were icon-only buttons with no text
              and no aria-label, so a screen reader announced three identical "button"s in
              a row on every provider in the results list — no way to tell map from
              directions from call. The provider's name is included so the names are also
              distinguishable between rows (2.4.4 Link Purpose in Context — A).
            */}
            {providerDetail?.lat && providerDetail?.lon && openInMapview && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary-selected"
                aria-label={`Show ${providerDetail?.name ?? "provider"} on the map`}
                onClick={() => openInMapview(index)}
              >
                <MapPin aria-hidden="true" className="size-4" />
              </Button>
            )}
            {providerDetail?.lat && providerDetail?.lon && getDirection && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary-selected"
                aria-label={`Get directions to ${providerDetail?.name ?? "provider"}`}
                onClick={() =>
                  getDirection(providerDetail?.lat, providerDetail?.lon)
                }
              >
                <Compass aria-hidden="true" className="size-4" />
              </Button>
            )}
            {providerDetail?.phone && callProvider && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary-selected"
                aria-label={`Call ${providerDetail?.name ?? "provider"}`}
                onClick={() => callProvider(providerDetail.phone)}
              >
                <Phone aria-hidden="true" className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProviderDetails;
